import type { IconName } from "@/assets/icons/Icon";
import { create } from "zustand";

export type ToastTone = "neutral" | "positive" | "negative" | "cautionary";

export interface ToastItem {
  id: number;
  message: string;
  icon?: IconName;
  tone: ToastTone;
  /** ms until auto-dismiss. */
  duration: number;
}

interface ToastState {
  toasts: ToastItem[];
  show: (
    message: string,
    options?: { tone?: ToastTone; icon?: IconName; duration?: number }
  ) => void;
  dismiss: (id: number) => void;
}

const ICON_BY_TONE: Record<ToastTone, IconName | undefined> = {
  neutral: undefined,
  positive: "circleCheckFill",
  negative: "circleExclamationFill",
  cautionary: "triangleExclamationFill",
};

let nextId = 1;

/**
 * The Wanted kit ships a presentational `Toast`; it has no queue or lifecycle.
 * This store is the missing 20 lines rather than a toast library — adding
 * `sonner` would put a second visual language next to the design system.
 */
export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  show: (message, options) => {
    const tone = options?.tone ?? "neutral";
    const item: ToastItem = {
      id: nextId++,
      message,
      tone,
      icon: options?.icon ?? ICON_BY_TONE[tone],
      duration: options?.duration ?? 4000,
    };
    set((s) => {
      /*
        같은 문구는 하나로 접어요.

        한 화면이 여러 엔드포인트를 동시에 부르면 실패도 동시에 옵니다 — api 인터셉터가
        요청마다 토스트를 띄우므로 시장 페이지 한 번 클릭에 **같은 문장 10개**가 쌓여
        화면 절반을 덮었어요(stage 실측). 같은 문구가 열 번 보이는 것은 한 번 보이는 것보다
        아무것도 더 알려주지 않으면서, 그 뒤에 오는 다른 오류를 밀어내 버립니다.

        타이머를 새로 만들지 않고 **기존 토스트를 맨 뒤로 옮깁니다**(id 를 새로 발급) —
        그래야 마지막 실패 시점부터 다시 카운트돼서, 계속 실패하는 동안 토스트가 사라지지
        않고 남아 있어요. 카운트를 세어 "×10" 으로 보여주는 건 하지 않습니다: 이 문구가
        몇 번 났는지는 운영자가 할 일을 바꾸지 않아요.
      */
      const rest = s.toasts.filter((t) => t.message !== message);
      return { toasts: [...rest, item] };
    });
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

/** Imperative helper for use outside React (api error interceptor, etc.). */
export const toast = {
  show: (message: string, options?: Parameters<ToastState["show"]>[1]) =>
    useToastStore.getState().show(message, options),
  success: (message: string) => useToastStore.getState().show(message, { tone: "positive" }),
  error: (message: string) => useToastStore.getState().show(message, { tone: "negative" }),
  warn: (message: string) => useToastStore.getState().show(message, { tone: "cautionary" }),
};
